import firebase from "firebase/compat/app";
import { firestore } from "../firebaseConfig";
import { Timestamp } from "../types";
import { doc, getDoc } from "firebase/firestore";
import { IWorkspaceSettings } from "../types/WorkspaceSettings";

type DocumentSnapshot = firebase.firestore.DocumentSnapshot;
type SnapshotOptions = firebase.firestore.SnapshotOptions;

class WorkspaceSettings implements IWorkspaceSettings {
  id: string;
  workspaceId: string;
  defaultPickupLocationId?: string;
  hidePickupStops?: boolean;
  hideDefaultPickupInOrderForm?: boolean;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;

  static collectionName = "workspace_settings";

  static converter = {
    toFirestore(config: WorkspaceSettings) {
      return config.data();
    },
    fromFirestore(snapshot: DocumentSnapshot, options: SnapshotOptions) {
      const data = snapshot.data(options) as IWorkspaceSettings;
      return new WorkspaceSettings({ ...data, id: snapshot.id });
    },
  };

  static default() {
    return new WorkspaceSettings({
      id: "",
      workspaceId: "",
      defaultPickupLocationId: undefined,
      hidePickupStops: false,
      hideDefaultPickupInOrderForm: false,
    });
  }

  static isNew(data: IWorkspaceSettings): boolean {
    return data.id.length === 0;
  }

  constructor({
    id,
    workspaceId,
    defaultPickupLocationId,
    hidePickupStops,
    hideDefaultPickupInOrderForm,
    createdAt,
    updatedAt,
  }: IWorkspaceSettings) {
    this.id = id;
    this.workspaceId = workspaceId;
    this.defaultPickupLocationId = defaultPickupLocationId;
    this.hidePickupStops = hidePickupStops;
    this.hideDefaultPickupInOrderForm = hideDefaultPickupInOrderForm;
    this.createdAt = createdAt;
    this.updatedAt = updatedAt;
  }

  clone() {
    return Object.assign(Object.create(Object.getPrototypeOf(this)), this);
  }

  setData(updates: Partial<IWorkspaceSettings>) {
    return Object.assign(this, updates);
  }

  data() {
    // Do not include undefined fields; Firestore rejects undefined values.
    const result: any = {
      // The document id equals the workspaceId: a workspace has exactly one
      // settings document (see getByWorkspaceId / save).
      workspaceId: this.workspaceId,
      hidePickupStops: this.hidePickupStops ?? false,
      hideDefaultPickupInOrderForm: this.hideDefaultPickupInOrderForm ?? false,
      createdAt: this.createdAt || null,
      updatedAt: this.updatedAt || null,
    };

    if (this.defaultPickupLocationId) {
      result.defaultPickupLocationId = this.defaultPickupLocationId;
    }

    return result;
  }

  static get = (workspaceId: string) => {
    return doc(
      firestore
        .collection(WorkspaceSettings.collectionName)
        .withConverter(WorkspaceSettings.converter),
      workspaceId,
    );
  };

  // A workspace has exactly one settings document, keyed by the workspaceId,
  // so we read it directly instead of querying (guarantees a singleton).
  static getByWorkspaceId = async (workspaceId: string) => {
    if (!workspaceId) return null;
    const snapshot = await getDoc(WorkspaceSettings.get(workspaceId));
    return snapshot.exists() ? snapshot.data() ?? null : null;
  };

  // Upsert the singleton settings document for a workspace. Uses the
  // workspaceId as the document id so concurrent saves can never create a
  // second document.
  static save = async (
    workspaceId: string,
    updates: Partial<Omit<IWorkspaceSettings, "id" | "workspaceId">>,
    existing?: WorkspaceSettings | null,
  ) => {
    const now = Timestamp.now();
    const config = new WorkspaceSettings({
      ...(existing ?? {}),
      ...updates,
      id: workspaceId,
      workspaceId,
      createdAt: existing?.createdAt ?? now,
      updatedAt: now,
    });

    await firestore
      .collection(WorkspaceSettings.collectionName)
      .doc(workspaceId)
      .withConverter(WorkspaceSettings.converter)
      .set(config, { merge: true });

    return config;
  };
}

export default WorkspaceSettings;
